import { useMemo, useCallback } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  TriggerHistory,
  TriggerHistoryFilters,
  TriggerHistoryStatus,
} from '@/types/monitoring';
import { useMonitoringStore } from '@/stores/monitoringStore';
import {
  fetchTriggerHistoriesPage,
  fetchTriggerHistory as fetchTriggerHistoryApi,
  fetchTriggerDetails,
  resubmitTriggerHistory,
  type TriggerHistoryPage,
} from '@/services/monitoringApi';
import { useMonitoringApiOptions } from './useMonitoringData';

/**
 * Default page size for trigger histories. Sized to keep the payload small
 * for noisy Skipped triggers while giving the user enough rows on first
 * load to see context. Tunable per `useInfiniteTriggerHistories` call.
 */
const DEFAULT_TRIGGER_HISTORY_TOP = 30;

/** Short stale time so toggling tabs refetches quickly. */
const TRIGGER_HISTORY_STALE_TIME = 5000;

/**
 * If a row appears with `status === 'running'`, poll faster. Trigger
 * evaluations are short-lived so this rarely matters in practice, but the
 * symmetry with `getInfiniteRunsRefetchInterval` keeps cadence intuitive.
 */
const ACTIVE_TRIGGER_POLL_INTERVAL = 3000;

export function getInfiniteTriggerHistoryRefetchInterval(
  pages: TriggerHistoryPage[] | undefined,
  autoRefreshEnabled: boolean,
  autoRefreshInterval: number
): number | false {
  const all = pages?.flatMap((page) => page.histories) ?? [];
  if (all.some((h) => h.status === 'running')) return ACTIVE_TRIGGER_POLL_INTERVAL;
  return autoRefreshEnabled ? autoRefreshInterval : false;
}

/** Client-side filter — server returns full set, UI hides what the user opts out of. */
export const filterTriggerHistories = (
  histories: TriggerHistory[],
  filters: TriggerHistoryFilters
): TriggerHistory[] => {
  return histories.filter((h) => {
    if (filters.statuses.length > 0 && !filters.statuses.includes(h.status)) {
      return false;
    }
    if (filters.clientTrackingId) {
      if (h.clientTrackingId !== filters.clientTrackingId) return false;
    }
    if (filters.startTime?.from) {
      if (new Date(h.startTime).getTime() < new Date(filters.startTime.from).getTime()) {
        return false;
      }
    }
    if (filters.startTime?.to) {
      if (new Date(h.startTime).getTime() > new Date(filters.startTime.to).getTime()) {
        return false;
      }
    }
    return true;
  });
};

type UseInfiniteTriggerHistoriesOptions = {
  /** Override page size sent to the runtime. */
  top?: number;
  /** Defaults to true; pass false to pause the query (e.g. when tab is hidden). */
  enabled?: boolean;
};

/**
 * Paginated trigger histories for a single trigger. Mirrors `useInfiniteRuns`:
 * - `useInfiniteQuery` for built-in dedup / pagination / cache.
 * - Auto-refresh respects the global `autoRefreshEnabled` + `autoRefreshInterval`.
 * - Client-side filter (status + tracking id + date) so the user can toggle
 *   statuses interactively without refetching.
 */
export const useInfiniteTriggerHistories = (
  triggerName: string | null | undefined,
  options?: UseInfiniteTriggerHistoriesOptions
) => {
  const triggerHistoryFilters = useMonitoringStore(
    (state) => state.triggerHistoryFilters
  );
  const autoRefreshEnabled = useMonitoringStore((state) => state.autoRefreshEnabled);
  const autoRefreshInterval = useMonitoringStore((state) => state.autoRefreshInterval);
  const { workflowName, projectName, appName, acquireToken, isReady } =
    useMonitoringApiOptions();

  const callerEnabled = options?.enabled ?? true;
  const top = options?.top ?? DEFAULT_TRIGGER_HISTORY_TOP;

  // Forward server-side filters to the runtime so the user is not limited to
  // matches that happen to be in the first client-side page. Status filtering
  // stays client-side because users toggle it interactively (see comment in
  // buildTriggerHistoriesUrl); clientTrackingId + startTime.from go on the
  // wire as $filter clauses.
  const clientTrackingIdFilter = triggerHistoryFilters.clientTrackingId;
  const startTimeGeFilter = triggerHistoryFilters.startTime?.from;

  const enabled = Boolean(
    isReady && callerEnabled && triggerName && projectName && appName && workflowName
  );

  const query = useInfiniteQuery({
    queryKey: [
      'triggerHistories',
      workflowName,
      projectName,
      appName,
      triggerName,
      top,
      clientTrackingIdFilter ?? null,
      startTimeGeFilter ?? null,
    ],
    queryFn: async ({ pageParam }): Promise<TriggerHistoryPage> => {
      if (!projectName || !workflowName || !appName || !triggerName) {
        throw new Error('Missing required context');
      }
      const token = await acquireToken();
      const listOpts: {
        top: number;
        clientTrackingId?: string;
        startTimeGe?: string;
      } = { top };
      if (clientTrackingIdFilter) listOpts.clientTrackingId = clientTrackingIdFilter;
      if (startTimeGeFilter) listOpts.startTimeGe = startTimeGeFilter;
      return fetchTriggerHistoriesPage(
        { projectName, appName, workflowName, accessToken: token },
        triggerName,
        listOpts,
        pageParam
      );
    },
    enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextLink,
    staleTime: TRIGGER_HISTORY_STALE_TIME,
    refetchInterval: callerEnabled
      ? (q) =>
          getInfiniteTriggerHistoryRefetchInterval(
            q.state.data?.pages,
            autoRefreshEnabled,
            autoRefreshInterval
          )
      : false,
  });

  const allHistories = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.histories);
  }, [query.data?.pages]);

  const filteredHistories = useMemo(() => {
    return filterTriggerHistories(allHistories, triggerHistoryFilters);
  }, [allHistories, triggerHistoryFilters]);

  return {
    ...query,
    data: filteredHistories,
    allHistories,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};

/**
 * Single trigger history record metadata.
 */
export const useTriggerHistoryRecord = (
  triggerName: string | null | undefined,
  historyName: string | null | undefined
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } =
    useMonitoringApiOptions();

  return useQuery({
    queryKey: [
      'triggerHistory',
      workflowName,
      projectName,
      appName,
      triggerName,
      historyName,
    ],
    queryFn: async (): Promise<TriggerHistory | null> => {
      if (!projectName || !workflowName || !appName || !triggerName || !historyName) {
        throw new Error('Missing required context');
      }
      const token = await acquireToken();
      return fetchTriggerHistoryApi(
        { projectName, appName, workflowName, accessToken: token },
        triggerName,
        historyName
      );
    },
    enabled: Boolean(
      isReady && triggerName && historyName && projectName && appName && workflowName
    ),
    staleTime: TRIGGER_HISTORY_STALE_TIME,
  });
};

/**
 * Inputs + outputs content for a trigger history row. Reuses
 * `fetchTriggerDetails` (the same endpoint used during run detail view).
 */
export const useTriggerHistoryContent = (
  triggerName: string | null | undefined,
  historyName: string | null | undefined
) => {
  const { workflowName, projectName, appName, acquireToken, isReady } =
    useMonitoringApiOptions();

  return useQuery({
    queryKey: [
      'triggerHistoryContent',
      workflowName,
      projectName,
      appName,
      triggerName,
      historyName,
    ],
    queryFn: async (): Promise<{ input?: unknown; output?: unknown } | null> => {
      if (!projectName || !workflowName || !appName || !triggerName || !historyName) {
        throw new Error('Missing required context');
      }
      const token = await acquireToken();
      return fetchTriggerDetails(
        { projectName, appName, workflowName, accessToken: token },
        historyName,
        triggerName
      );
    },
    enabled: Boolean(
      isReady && triggerName && historyName && projectName && appName && workflowName
    ),
    // Content is immutable for completed histories; keep it warm.
    staleTime: 60 * 1000,
  });
};

/**
 * Resubmits a trigger history row — re-runs the workflow with the original
 * trigger payload. On success invalidates both the runs and trigger-history
 * query keys so the new run shows up immediately.
 */
export const useResubmitTriggerHistory = () => {
  const queryClient = useQueryClient();
  const { workflowName, projectName, appName, acquireToken, isReady } =
    useMonitoringApiOptions();

  const mutation = useMutation({
    mutationFn: async ({
      triggerName,
      historyName,
    }: {
      triggerName: string;
      historyName: string;
    }): Promise<{ success: boolean; error?: string }> => {
      if (!isReady || !projectName || !workflowName || !appName) {
        return { success: false, error: 'Workflow context not ready' };
      }
      try {
        const token = await acquireToken();
        await resubmitTriggerHistory(
          { projectName, appName, workflowName, accessToken: token },
          triggerName,
          historyName
        );
        return { success: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        return { success: false, error: message };
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['runs', workflowName] });
        queryClient.invalidateQueries({ queryKey: ['infiniteRuns', workflowName] });
        queryClient.invalidateQueries({
          queryKey: ['triggerHistories', workflowName],
        });
      }
    },
  });

  // Stable function so callers don't re-render on every render.
  const resubmit = useCallback(
    (input: { triggerName: string; historyName: string }) =>
      mutation.mutateAsync(input),
    [mutation]
  );

  return { ...mutation, resubmit };
};

/** Convenience selector — list of statuses available in the filter UI. */
export const TRIGGER_HISTORY_STATUS_OPTIONS: ReadonlyArray<{
  value: TriggerHistoryStatus;
  label: string;
}> = [
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'running', label: 'Running' },
];
